import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowDownToLine, Banknote, ClipboardCheck, FileClock, Landmark } from "lucide-react";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";
import { cn } from "@/lib/utils";

import { CashCounts } from "./cash-counts";
import { CashMovements } from "./cash-movements";
import type { FinanceModal } from "./types";

export function CashBoxPanel({
	date,
	onModal,
}: {
	date: string;
	onModal: (modal: FinanceModal) => void;
}) {
	const { data } = useSuspenseQuery(orpc.financeiro.cash.list.queryOptions({ input: { date } }));

	return (
		<div className="workspace-frame min-w-0 @container">
			<section className="min-w-0">
				<header className="flex min-w-0 flex-col gap-5 border-b border-border p-5 @2xl:flex-row @2xl:items-center @2xl:justify-between">
					<div>
						<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
							{data.account.name}
						</p>
						<p
							className={cn(
								"mt-2 text-3xl font-semibold leading-none tracking-tight tabular-nums",
								data.balance_cents < 0 ? "text-destructive" : "text-foreground",
							)}
						>
							{FinanceiroLabels.money(data.balance_cents)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							Saldo ao fim de {format.date(date)}
						</p>
					</div>
					<div className="grid min-w-0 grid-cols-2 gap-2 @4xl:flex @4xl:flex-wrap">
						<Button
							size="sm"
							variant="outline"
							className="min-w-0 w-full px-3 @4xl:w-auto"
							onClick={() => onModal({ kind: "cash-movement", movementKind: "deposit", date })}
						>
							<Landmark />
							Depósito
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="min-w-0 w-full px-3 @4xl:w-auto"
							onClick={() => onModal({ kind: "cash-movement", movementKind: "withdrawal", date })}
						>
							<ArrowDownToLine />
							Retirada
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="min-w-0 w-full px-3 @4xl:w-auto"
							onClick={() => onModal({ kind: "cash-bill-payment", date })}
						>
							<FileClock />
							Pagar conta
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="min-w-0 w-full px-3 @4xl:w-auto"
							onClick={() =>
								onModal({
									kind: "cash-count",
									date,
									balanceCents: data.balance_cents,
								})
							}
						>
							<ClipboardCheck />
							Contar caixa
						</Button>
					</div>
				</header>
			</section>

			<section className="min-w-0">
				<div className="p-4">
					<div className="flex items-baseline justify-between gap-3">
						<h3 className="text-sm font-semibold text-foreground">Movimentos do caixa</h3>
						<span className="text-xs text-muted-foreground">{data.data.length} lançamentos</span>
					</div>

					{data.data.length === 0 && data.counts.length === 0 && (
						<EmptyState icon={Banknote} title="Nenhum movimento neste dia" />
					)}

					<CashMovements movements={data.data} onModal={onModal} />
					<CashCounts counts={data.counts} />
				</div>
			</section>
		</div>
	);
}
